import { api } from './client';
import type { Province, CreateProvinceRequest } from '../types/province';

let cachedProvince: Province | null = null;
let cachedETag: string | null = null;

export async function getProvince(
  worldId: string,
  provinceId: string
): Promise<{
  province: Province | null;
  error: string | null;
  unchanged: boolean;
}> {
  const headers: Record<string, string> = {};
  if (cachedETag) {
    headers['If-None-Match'] = cachedETag;
  }

  const { data, error, status, etag } = await api.get<Province>(
    `/api/worlds/${worldId}/provinces/${provinceId}`,
    headers
  );

  if (status === 304) {
    return { province: cachedProvince, error: null, unchanged: true };
  }

  if (error || !data) {
    return {
      province: null,
      error: error || 'Failed to load province',
      unchanged: false,
    };
  }

  cachedProvince = data;
  cachedETag = etag ?? null;

  return { province: data, error: null, unchanged: false };
}

export function clearProvinceCache(): void {
  cachedProvince = null;
  cachedETag = null;
}

interface CreateProvinceResponse {
  provinceId: string;
  token: {
    accessToken: string;
    provincesByWorld: Record<string, string>;
  };
}

export async function createProvince(
  worldId: string,
  request: CreateProvinceRequest
): Promise<{ provinceId: string | null; token: CreateProvinceResponse['token'] | null; error: string | null }> {
  const { data, error } = await api.post<CreateProvinceResponse>(
    `/api/worlds/${worldId}/provinces`,
    request
  );

  if (error) {
    return { provinceId: null, token: null, error };
  }

  return { provinceId: data?.provinceId || null, token: data?.token || null, error: null };
}
